import Vue from "vue";
import LoadScript from "vue-plugin-load-script";
import router from "./router";
import mgr from "./security";
import axios from "axios";
import moment from "moment";
import VJstree from "vue-jstree";
import UUID from "vue-uuid";

Vue.config.productionTip = false;
Vue.use(LoadScript);
Vue.use(UUID);
Vue.component("v-jstree", VJstree);

const globalData = {
    isAuthenticated: false,
    user: "",
    profiles: [],
    mgr: mgr,
    siteConfig: SiteConfig,
};

// ISS-36 (CR-BudgetReport · QA BUG-01) หน้าแรกของ user ที่มีแต่สิทธิ์ Budget Approval
// "/" ผูกกับ Dashboard ที่ต้องมี role DASHBOARD — role ของ budget ไม่มี จึงเคยเด้ง /Error/Unauthorized
// เงื่อนไขสิทธิ์ใช้ชุดเดียวกับเมนู Budget Approval ใน App.vue · คนที่มี DASHBOARD ยังเข้า Dashboard ตามเดิม
const BUDGET_LANDING_PATH = "/BudgetPlan/Job";
const BUDGET_MENU_ROLES = [
    ["BUDGET_PLAN_USER", "COMPANY_PROFILE_TYPE_BUDGET"],
    ["BUDGET_PLAN_REVIEWER", "COMPANY_PROFILE_TYPE_BUDGET"],
    ["BUDGET_PLAN_MANAGER", "COMPANY_PROFILE_TYPE_BUDGET"],
];

const globalMethods = {
    /**
     * ISS-36: หน้าแรกแทน "/" ของ user ที่มีแต่สิทธิ์ Budget Approval — ไม่ใช่ user กลุ่มนี้คืน null
     * เรียกจาก 2 ที่: router.beforeEach (เข้า "/" ระหว่างใช้งาน / หลัง login)
     * และ App.vue mounted (เปิด "/" ใหม่ทั้งหน้า — router นำทางครั้งแรกก่อน beforeEach ถูกผูก)
     * โหลด user + profiles เองถ้ายังไม่มี เพราะทั้ง 2 จุดอาจถูกเรียกก่อน authenticate เสร็จ
     */
    async budgetLandingPath() {
        if (!this.user) {
            const user = await this.getUser();
            if (!user || user.expires_at <= moment().unix()) return null;
            this.user = user;   // interceptor ของ axios ใช้ค่านี้แนบ token ให้ loadProfiles
        }
        await this.loadProfiles();
        // canShow ให้ super_admin ผ่านเสมอ -> super_admin ไม่ถูกพามาที่นี่
        if (this.canShow(["DASHBOARD"]) || !this.canShowExSuperAdmin(BUDGET_MENU_ROLES)) return null;
        return BUDGET_LANDING_PATH;
    },
    async authenticate(returnPath) {

        const user = await this.$root.getUser(); //see if the user details are in local storage

        if (user && user.expires_at > moment().unix()) {
            this.isAuthenticated = true;
            this.user = user;
        } else {
            await this.$root.signIn(returnPath);
        }
    },
    async getUser() {
        try {
            let user = await this.mgr.getUser();
            return user;
        } catch (err) {
            console.log(err);
        }
    },
    async loadProfiles() {
        // โหลด profiles จาก API ถ้ายังไม่ถูกโหลด — ป้องกัน race condition
        // ระหว่าง redirect logic กับการโหลด profile
        if (this.profiles && this.profiles.length > 0) {
            return this.profiles;
        }
        try {
            const response = await axios.get("/Api/CompanyProfile/GetMyProfiles");
            if (response.status == 200) {
                this.profiles = response.data;
            }
        } catch (err) {
            console.log("loadProfiles error", err);
        }
        return this.profiles;
    },
    signIn(returnPath) {

        this.mgr
            .clearStaleState()
            .then(() => {
                console.log("clearState success");
                returnPath
                    ?
                    this.mgr.signinRedirect({
                        state: returnPath,
                    }) :
                    this.mgr.signinRedirect();
            })
            .catch((e) => {
                console.log("clearStateState error", e.message);
            });
    },
    signOut() {
        this.mgr.clearStaleState().then(() => {
            console.log("clearState success");
        });

        return this.mgr.signoutRedirect();
    },
    async canAccessPage(to) {


        if (
            to.meta.requiredAuth &&
            to.meta.requiredRoles &&
            to.meta.requiredRoles.length > 0
        ) {
            let user = await this.mgr.getUser();

            // Check if super_admin should be excluded from this route
            const excludeSuperAdmin = to.meta.excludeSuperAdmin === true;

            // Check if user's profile type is excluded from this route
            // (super_admin จะข้ามการเช็คนี้ — เห็นทุกหน้าเสมอ)
            const excludedProfileTypes = to.meta.excludedProfileTypes || [];
            const isSuperAdmin = Array.isArray(user.profile.role)
                ? user.profile.role.indexOf("super_admin") !== -1
                : user.profile.role === "super_admin";
            if (excludedProfileTypes.length > 0 && !isSuperAdmin) {
                const profiles = this.profiles;
                if (profiles && Array.isArray(profiles)) {
                    for (let p = 0; p < profiles.length; p++) {
                        if (excludedProfileTypes.indexOf(profiles[p].profileTypeCode) !== -1) {
                            return false;
                        }
                    }
                }
            }

            // Allow access if user has any profile in requiredProfileTypes (independent of roles)
            const requiredProfileTypes = to.meta.requiredProfileTypes || [];
            if (requiredProfileTypes.length > 0) {
                const profiles = this.profiles;
                if (profiles && Array.isArray(profiles)) {
                    for (let p = 0; p < profiles.length; p++) {
                        if (requiredProfileTypes.indexOf(profiles[p].profileTypeCode) !== -1) {
                            return true;
                        }
                    }
                }
            }

            if (Array.isArray(user.profile.role)) {
                for (let i = 0; i < user.profile.role.length; i++) {
                    const role = user.profile.role[i];
                    if (role === "super_admin" && !excludeSuperAdmin) {
                        return true;
                    }

                    for (let j = 0; j < to.meta.requiredRoles.length; j++) {
                        const req = to.meta.requiredRoles[j];
                        if (role === req) {
                            return true;
                        }
                    }
                }
            } else {
                const role = user.profile.role;
                if (role === "super_admin" && !excludeSuperAdmin) {
                    return true;
                }

                for (let j = 0; j < to.meta.requiredRoles.length; j++) {
                    const req = to.meta.requiredRoles[j];
                    if (role === req) {
                        return true;
                    }
                }
            }
            return false;
        }
        return true;
    },
    canShow(roles) {
        let user = this.user;
        let profiles = this.profiles; 
        if (user) {
            if (Array.isArray(roles)) {
                // กรณีส่งถามสิทธิ์เข้ามาเป็น array
                for (let i = 0; i < roles.length; i++) {
                    const req = roles[i];
                    if (Array.isArray(req)) {
                        if (Array.isArray(user.profile.role)) {
                            for (let j = 0; j < user.profile.role.length; j++) {
                                const role = user.profile.role[j]; 
                                if (role === "super_admin") {
                                    return true;
                                }
                            }
                        } else {
                            const role = user.profile.role;
                            if (role === "super_admin") {
                                return true;
                            }
                        }

                        // กรณีส่งถามสิทธิ์เข้ามาเป็น array ให้ตรวจสอบสิทธิ์จาก profiles โดยต้องตรวจสอบ type
                        // 0: role
                        // 1: type
                        for (let j = 0; j < profiles.length; j++) {
                            const profile = profiles[j];
                            if (profile.profileTypeCode == req[1]) {
                                for (let k = 0; k < profile.roles.length; k++) {
                                    const preq = profile.roles[k];
                                    if (preq == req[0]) {
                                        return true;
                                    }
                                }
                            }
                        }
                    } else if (typeof req === "string") {
                        // กรณีส่งถามสิทธิ์เข้ามาเป็น string ให้ตรวจสอบสิทธิ์จาก user role โดยไม่ต้องตรวจสอบ type
                        if (Array.isArray(user.profile.role)) {
                            for (let j = 0; j < user.profile.role.length; j++) {
                                const role = user.profile.role[j];
                                if (role === "super_admin") {
                                    return true;
                                } else if (role === req) {
                                    return true;
                                }
                            }
                        } else {
                            const role = user.profile.role;
                            if (role === "super_admin") {
                                return true;
                            } else if (role === req) {
                                return true;
                            }
                        }
                    }
                }

                // if (Array.isArray(user.profile.role)) {
                //   for (let i = 0; i < user.profile.role.length; i++) {
                //     const role = user.profile.role[i];
                //     if (role === "super_admin") {
                //       return true;
                //     }

                //     for (let j = 0; j < roles.length; j++) {
                //       const req = roles[j];
                //       if (role === req) {
                //         return true;
                //       }
                //     }
                //   }
                // } else {
                //   const role = user.profile.role;
                //   if (role === "super_admin") {
                //     return true;
                //   }

                //   for (let j = 0; j < roles.length; j++) {
                //     const req = roles[j];
                //     if (role === req) {
                //       return true;
                //     }
                //   }
                // }
            } else if (typeof roles === "string") {
                // กรณีส่งถามสิทธิ์เข้ามาเป็น string ให้ตรวจสอบสิทธิ์จาก user role โดยไม่ต้องตรวจสอบ type
                if (Array.isArray(user.profile.role)) {
                    for (let i = 0; i < user.profile.role.length; i++) {
                        const role = user.profile.role[i];
                        if (role === "super_admin") {
                            return true;
                        } else if (role === roles) {
                            return true;
                        }
                    }
                } else {
                    const role = user.profile.role;
                    if (role === "super_admin") {
                        return true;
                    } else if (role === roles) {
                        return true;
                    }
                }
            }
        }
        return false;
    },
    canShowExSuperAdmin(roles) {
        let user = this.user;
        let profiles = this.profiles;
        if (user) {
            if (Array.isArray(roles)) {
                // กรณีส่งถามสิทธิ์เข้ามาเป็น array
                for (let i = 0; i < roles.length; i++) {
                    const req = roles[i];
                    if (Array.isArray(req)) {
                        // กรณีส่งถามสิทธิ์เข้ามาเป็น array ให้ตรวจสอบสิทธิ์จาก profiles โดยต้องตรวจสอบ type
                        // 0: role
                        // 1: type
                        for (let j = 0; j < profiles.length; j++) {
                            const profile = profiles[j];
                            if (profile.profileTypeCode == req[1]) {
                                for (let k = 0; k < profile.roles.length; k++) {
                                    const preq = profile.roles[k];
                                    if (preq == req[0]) {
                                        return true;
                                    }
                                }
                            }
                        }
                    } else if (typeof req === "string") {
                        // กรณีส่งถามสิทธิ์เข้ามาเป็น string ให้ตรวจสอบสิทธิ์จาก user role โดยไม่ต้องตรวจสอบ type
                        if (Array.isArray(user.profile.role)) {
                            for (let j = 0; j < user.profile.role.length; j++) {
                                const role = user.profile.role[j];
                                if (role === req) {
                                    return true;
                                }
                            }
                        } else {
                            const role = user.profile.role;
                            if (role === req) {
                                return true;
                            }
                        }
                    }
                }
            } else if (typeof roles === "string") {
                // กรณีส่งถามสิทธิ์เข้ามาเป็น string ให้ตรวจสอบสิทธิ์จาก user role โดยไม่ต้องตรวจสอบ type
                if (Array.isArray(user.profile.role)) {
                    for (let i = 0; i < user.profile.role.length; i++) {
                        const role = user.profile.role[i];
                        if (role === roles) {
                            return true;
                        }
                    }
                } else {
                    const role = user.profile.role;
                    if (role === roles) {
                        return true;
                    }
                }
            }
        }
        return false;
    },
    /**
     * Check if user has Budget Plan profile (any role: USER, REVIEWER, MANAGER)
     * Used to hide menus from Budget Plan profile users
     * Note: super_admin users always return false to see all menus
     */
    hasBudgetPlanProfile() {
        // super_admin should see all menus regardless of company profiles
        // Handle both array and string cases for role
        if (this.user && this.user.profile) {
            var role = this.user.profile.role;
            if (Array.isArray(role)) {
                for (var i = 0; i < role.length; i++) {
                    if (role[i] === 'super_admin') {
                        return false;
                    }
                }
            } else if (role === 'super_admin') {
                return false;
            }
        }

        var profiles = this.profiles;
        if (profiles && Array.isArray(profiles)) {
            for (var j = 0; j < profiles.length; j++) {
                var profile = profiles[j];
                if (profile.profileTypeCode === 'COMPANY_PROFILE_TYPE_BUDGET') {
                    return true;
                }
            }
        }
        return false;
    },
    /**
     * CR-FormSummaryStock: ตรวจว่า user ผูกโปรไฟล์ "พิมพ์เอกสารสรุปข้อมูล" ไว้หรือไม่
     *
     * ตาม requirement: ผู้ที่มีสิทธิ์ "ผู้ดูแลระบบ" ทั่วไปแต่ยังไม่ได้ผูกโปรไฟล์นี้ จะไม่เห็นเมนู
     * -> super_admin ก็ไม่ bypass (ต่างจาก hasBudgetPlanProfile ที่ return false ให้ super_admin)
     */
    hasFormSummaryStockProfile() {
        var profiles = this.profiles;
        if (profiles && Array.isArray(profiles)) {
            for (var j = 0; j < profiles.length; j++) {
                if (profiles[j].profileTypeCode === 'COMPANY_PROFILE_TYPE_FORM_SUMMARY_STOCK') {
                    return true;
                }
            }
        }
        return false;
    },
    /**
     * Check if user has BR (factory) profile
     * Used to hide menus from BR factory admin users
     * Note: super_admin users always return false to see all menus
     */
    hasBrProfile() {
        if (this.user && this.user.profile) {
            var role = this.user.profile.role;
            if (Array.isArray(role)) {
                for (var i = 0; i < role.length; i++) {
                    if (role[i] === 'super_admin') {
                        return false;
                    }
                }
            } else if (role === 'super_admin') {
                return false;
            }
        }

        var profiles = this.profiles;
        if (profiles && Array.isArray(profiles)) {
            for (var j = 0; j < profiles.length; j++) {
                if (profiles[j].profileTypeCode === 'COMPANY_PROFILE_TYPE_BR') {
                    return true;
                }
            }
        }
        return false;
    },
    /**
     * Check if user has Agent (ตัวแทนจำหน่าย) profile (any role under COMPANY_PROFILE_TYPE_AGENT)
     * Used to grant access to agent-side score reports without requiring a specific role
     */
    hasAgentProfile() {
        var profiles = this.profiles;
        if (profiles && Array.isArray(profiles)) {
            for (var j = 0; j < profiles.length; j++) {
                if (profiles[j].profileTypeCode === 'COMPANY_PROFILE_TYPE_AGENT') {
                    return true;
                }
            }
        }
        return false;
    },
    /**
     * Check if user has Stock Cover Days (Profile 13) company profile.
     * G6 (spec sheet7): ต้องมี Profile 13 ถึงจะเห็นเมนู SCVD — ใช้กับ super_admin ด้วย (ไม่ bypass).
     */
    hasScvdProfile() {
        var profiles = this.profiles;
        if (profiles && Array.isArray(profiles)) {
            for (var j = 0; j < profiles.length; j++) {
                if (profiles[j].profileTypeCode === 'COMPANY_PROFILE_TYPE_SCVD') {
                    return true;
                }
            }
        }
        return false;
    },
    /**
     * Check if user has at least one CompanyProfile
     * Used to hide agent-score sections from users with no company profile mapping
     * Note: super_admin returns true so the section stays visible for them
     */
    hasAnyCompanyProfile() {
  
        var profiles = this.profiles;
        var ret =  profiles && Array.isArray(profiles) && profiles.length > 0; 

        return ret ;
    },
};

let v = new Vue({
    el: "#app",
    template: "<div><router-view></router-view></div>",
    router,
    data: globalData,
    methods: globalMethods,
});

router.beforeEach(async(to, from, next) => {
    let app = router.app.$data || {
        isAuthenticated: false,
    };

    await v.loadProfiles();  
    var is_super_admin = false ;

    if (v.user && v.user.profile) {
        var role = v.user.profile.role;
        if (Array.isArray(role)) {
            for (var i = 0; i < role.length; i++) {
                if (role[i] === 'super_admin') {
                    is_super_admin = true;
                    break ;
                }
            }
        } 
    }
    
    if (app.isAuthenticated) {
        
        // โหลด profiles ก่อนเพื่อให้ hasBrProfile() ทำงานถูกต้อง (ป้องกัน race condition)
       
        
        // AGENT_REPORT / BR_REPORT role → redirect "/" to AgentScoreDashboard
        // ยกเว้น COMPANY_PROFILE_TYPE_BR (แอดมินโรงงาน) — ห้ามเข้าหน้านี้
        if (!is_super_admin && to.path === "/" && v.canShowExSuperAdmin(["AGENT_REPORT", "BR_REPORT"]) && !v.hasBrProfile()) {
            next("/AgentScoreDashboard");
            return;
        }
        // CR-StockCoverDays (QA BUG-01): user SCVD ที่ไม่มีสิทธิ์หน้าแรก (DASHBOARD) → พาไป SCVD Dashboard แทนหน้า Unauthorized
        if (!is_super_admin && to.path === "/" && !v.canShowExSuperAdmin(["DASHBOARD"]) && v.canShowExSuperAdmin(["SCVD_ADMIN", "SCVD_TEAM_EDIT", "SCVD_VIEWER"])) {
            next("/StockCoverDays/Dashboard");
            return;
        }
        // ISS-36 (CR-BudgetReport · QA BUG-01) user ของ Budget Approval ไม่มีสิทธิ์ Dashboard
        // เข้า "/" แล้วเคยตกไป /Error/Unauthorized → พาไปใบงาน Budget แทน
        if (!is_super_admin && to.path === "/") {
            const landing = await v.budgetLandingPath();
            if (landing) {
                next(landing);
                return;
            }
        }
        //already signed in, we can navigate anywhere
        if (await v.canAccessPage(to)) {
            next();
        } else {
            router.push({
                path: "/Error/Unauthorized",
            });
        }
    } else if (to.matched.some((record) => record.meta.requiredAuth)) {
        //authentication is required. Trigger the sign in process, including the return URI
        router.app.authenticate(to.path).then(async() => {
            // โหลด profiles ก่อนเพื่อให้ hasBrProfile() ทำงานถูกต้อง (ป้องกัน race condition)
            await v.loadProfiles();
            // AGENT_REPORT / BR_REPORT role → redirect "/" to AgentScoreDashboard
            // ยกเว้น COMPANY_PROFILE_TYPE_BR (แอดมินโรงงาน) — ห้ามเข้าหน้านี้
            if (!is_super_admin && to.path === "/" && v.canShowExSuperAdmin(["AGENT_REPORT", "BR_REPORT"]) && !v.hasBrProfile()) {
                next("/AgentScoreDashboard");
                return;
            }
            // CR-StockCoverDays (QA BUG-01): user SCVD ที่ไม่มีสิทธิ์หน้าแรก (DASHBOARD) → พาไป SCVD Dashboard
            if (!is_super_admin && to.path === "/" && !v.canShowExSuperAdmin(["DASHBOARD"]) && v.canShowExSuperAdmin(["SCVD_ADMIN", "SCVD_TEAM_EDIT", "SCVD_VIEWER"])) {
                next("/StockCoverDays/Dashboard");
                return;
            }
            // ISS-36 — เหมือนด้านบน (branch ที่เพิ่ง login เสร็จ)
            if (!is_super_admin && to.path === "/") {
                const landing = await v.budgetLandingPath();
                if (landing) {
                    next(landing);
                    return;
                }
            }
            if (await v.canAccessPage(to)) {
                next();
            } else {
                router.push({
                    path: "/Error/Unauthorized",
                });
            }
        });
    } else {
        //No auth required. We can navigate
        if (await v.canAccessPage(to)) {
            next();
        } else {
            router.push({
                path: "/Error/Unauthorized",
            });
        }
    }
});

// CR-StockCoverDays — /Monitoring email gate: identity email must be in AppSettings.MonitorEmail
// (server-verified via /Api/Monitor/access_check). Not a monitor → redirect home. Result cached on $root.
router.beforeEach(async (to, from, next) => {
    if (!to.matched.some((r) => r.meta && r.meta.requiredMonitorEmail)) {
        next();
        return;
    }
    try {
        if (v.$root.isMonitor === undefined || v.$root.isMonitor === null) {
            const res = await axios.get("/Api/Monitor/access_check");
            v.$root.isMonitor = !!(res.data && res.data.isMonitor);
        }
    } catch (e) {
        v.$root.isMonitor = false;
    }
    if (v.$root.isMonitor) next();
    else next("/");
});

axios.interceptors.request.use(
    (config) => {
        const user = v.$root.user;
        if (user) {
            const authToken = user.access_token;
            if (authToken) {
                config.headers.Authorization = `Bearer ${authToken}`;
            }
        }
        return config;
    },
    (err) => {
        //What do you want to do when a call fails?
    }
);

mgr.events.addUserLoaded(async function() {
    // console.log("UserLoaded (Old Token)", v.$root.user ? v.$root.user.access_token : "-");
    v.$root.user = await v.$root.getUser();
    // console.log("UserLoaded (New Token)", v.$root.user.access_token);
});

mgr.events.addAccessTokenExpired(function() {
    console.log("AccessTokenExpired");
});


// WEBPACK FOOTER //
// ./App/index.js